import React, { useCallback, useMemo } from "react";
import { Calendar, CalendarProps } from "react-calendar";
import styles from "./cp-calendar.module.css";
import { CpIcon } from "../cp-icon/cp-icon.component";
import {
  parseDateStr,
  isSameDay,
  isDateBetween,
  toStringValue,
} from "./cp-calendar.helpers";

type CommonProps = {
  className?: string;
  minDate?: string;
  maxDate?: string;
};

type RangeProps = {
  selectRange: true;
  /**
   * UX Wants different styling to indicate when a range is relative/dynamic and not a fixed range, so use this to enable that styling.
   * @default false
   */
  isRelativeRange?: boolean;
  value?: [string, string];
  onChange?: (value: [string, string]) => void;
};

type SingleDateProps = {
  selectRange?: false;
  value?: string;
  onChange?: (value: string) => void;
};

export type CpCalendarProps = CommonProps & (RangeProps | SingleDateProps);

export function CpCalendar(props: CpCalendarProps) {
  const { className, selectRange, value, onChange, minDate, maxDate } = props;
  const isRelativeRange = selectRange && !!props.isRelativeRange;

  // Convert string dates to Date objects for react-calendar
  const calendarValue = useMemo((): CalendarProps["value"] => {
    if (!value) return null;

    if (Array.isArray(value)) {
      const [start, end] = value;
      const startDate = parseDateStr(start);
      const endDate = parseDateStr(end);

      // Handle partial ranges - if only start is set, return just the start date
      // This works around react-calendar's allowPartialRange issues
      if (startDate && !endDate) {
        return startDate;
      }
      // If both dates exist, return as range
      if (startDate && endDate) {
        return [startDate, endDate];
      }
      // If only end date
      if (!startDate && endDate) {
        return endDate;
      }
      return null;
    }

    return parseDateStr(value);
  }, [value]);

  const minDateObject = useMemo(() => {
    return minDate ? parseDateStr(minDate) : undefined;
  }, [minDate]);

  const maxDateObject = useMemo(() => {
    return maxDate ? parseDateStr(maxDate) : undefined;
  }, [maxDate]);

  // This is the date that will be used to show what month to open the calendar on
  const defaultActiveStartDate = useMemo(() => {
    const defaultDate = new Date();
    if (selectRange && Array.isArray(value)) {
      const [start] = value;
      const startDate = parseDateStr(start);
      return startDate || defaultDate;
    }

    if (!selectRange && typeof value === "string") {
      const singleDate = parseDateStr(value);
      return singleDate || defaultDate;
    }

    return defaultDate;
  }, [selectRange, value]);

  const handleChange = (newValue: CalendarProps["value"]) => {
    if (!onChange) return;

    // Handle cleared selection
    if (!newValue) {
      if (selectRange) {
        onChange(["", ""]);
      } else {
        onChange("");
      }
      return;
    }

    // Handle range selection
    if (selectRange) {
      if (Array.isArray(newValue)) {
        const [start, end] = newValue;
        const rangeValue: [string, string] = [
          toStringValue(start),
          toStringValue(end),
        ];
        onChange?.(rangeValue);
        return;
      } else {
        // Single date in range mode - this happens during partial selection
        // Check current state to determine if this is start or end
        if (Array.isArray(value) && value[0] && !value[1]) {
          // We have start date, this click is the end date
          onChange?.([value[0], toStringValue(newValue)]);
        } else {
          // This is the start date
          onChange?.([toStringValue(newValue), ""]);
        }
        return;
      }
    }

    // Handle single date selection
    if (!selectRange && !Array.isArray(newValue)) {
      onChange?.(toStringValue(newValue));
    }
  };

  // Render visual connector between selected range dates
  const renderRangeContent = useCallback(
    ({ date, view }: { date: Date; view: string }) => {
      // Only show in month view for range selections
      if (!selectRange || view !== "month" || !Array.isArray(value)) {
        return null;
      }

      const [startStr, endStr] = value;
      if (!startStr || !endStr) return null;

      const startDate = parseDateStr(startStr);
      const endDate = parseDateStr(endStr);

      if (!startDate || !endDate || isSameDay(startDate, endDate)) return null;

      return isDateBetween(date, startDate, endDate) ? (
        <div className={styles.rangeConnection} />
      ) : null;
    },
    [selectRange, value],
  );

  return (
    <Calendar
      /*
        react-calendar has issues managing its internal value state with the values we give it
        For example without this key try clicking in the calendar to set a start value in a range and then have something update the value to a range (like a button) and you'll notice the issue.
      */
      key={Array.isArray(value) ? value.join("-") : value}
      value={calendarValue}
      onChange={handleChange}
      selectRange={selectRange}
      returnValue={selectRange ? "range" : "start"}
      allowPartialRange
      className={`${styles.reactCalendar} ${isRelativeRange ? styles.relativeRange : ""} ${className || ""}`}
      calendarType="gregory"
      prevLabel={<CpIcon name="caret-large-left" />}
      nextLabel={<CpIcon name="caret-large-right" />}
      tileContent={selectRange ? renderRangeContent : undefined}
      formatYear={(_, date) => date.getFullYear().toString()}
      formatMonth={(locale, date) =>
        date.toLocaleDateString(locale, { month: "short" })
      }
      prev2Label={null}
      next2Label={null}
      formatShortWeekday={(locale, date) => {
        const day = date.toLocaleDateString(locale, { weekday: "short" });
        return day.charAt(0).toUpperCase();
      }}
      showFixedNumberOfWeeks={true}
      minDate={minDateObject || undefined}
      maxDate={maxDateObject || undefined}
      defaultActiveStartDate={defaultActiveStartDate}
    />
  );
}
